import compact from 'lodash/compact';

import { Nullable } from '@/models/Model';
import {
  ExamSchedulingStatusEnum,
  ScheduledLessonExamExam,
} from '@/models/ScheduledLessonExam';
import { StudentExamCorrectionStatusEnum } from '@/models/StudentExam';
import {
  groupStudentExamSources,
  GroupStudentExamSourcesParams,
} from '@/utils/student-exam/groupStudentExamSources';
import {
  StudentExamRecord,
  StudentExamRecordSource,
  StudentExamSources,
} from '@/utils/student-exam/studentExamRecordTypes';
import { ROUTES } from '@/utils/routes';

type ToStudentExamRecordsParams = GroupStudentExamSourcesParams & {
  klassId: number;
};

type StudentExamRecordResolver = (
  sources: StudentExamSources,
) => Nullable<StudentExamRecord>;

const toRecordBase = (exam: ScheduledLessonExamExam) => ({
  id: `exam-${exam.id}`,
  exam,
  href: null,
});

const toCorrectedRecord: StudentExamRecordResolver = ({ exam, studentExam }) =>
  studentExam?.correctedAt
    ? {
        ...toRecordBase(exam),
        status: StudentExamCorrectionStatusEnum.CORRECTED,
        datetime: studentExam.correctedAt,
      }
    : null;

const toPendingRecord: StudentExamRecordResolver = ({ exam, studentExam }) =>
  studentExam?.finishedAt
    ? {
        ...toRecordBase(exam),
        status: StudentExamCorrectionStatusEnum.PENDING,
        datetime: studentExam.finishedAt,
      }
    : null;

const toScheduledRecord: StudentExamRecordResolver = ({
  exam,
  studentExam,
  scheduledLessonExam,
}) => {
  const scheduledAt =
    scheduledLessonExam?.examSchedule?.opensAt ?? studentExam?.opensAt;

  return scheduledAt
    ? {
        ...toRecordBase(exam),
        status: ExamSchedulingStatusEnum.SCHEDULED,
        datetime: scheduledAt,
        closesAt:
          scheduledLessonExam?.examSchedule?.closesAt ??
          studentExam?.closesAt ??
          null,
      }
    : null;
};

const toNotScheduledRecord = (
  { exam, scheduledLessonExam }: StudentExamSources,
  klassId: number,
): StudentExamRecord => ({
  ...toRecordBase(exam),
  status: ExamSchedulingStatusEnum.NOT_SCHEDULED,
  datetime: scheduledLessonExam?.datetime ?? '',
  href: scheduledLessonExam
    ? ROUTES.EXAM_SCHEDULING.SCHEDULE(klassId, exam.id, scheduledLessonExam.id)
    : null,
});

const recordResolvers: StudentExamRecordResolver[] = [
  toCorrectedRecord,
  toPendingRecord,
  toScheduledRecord,
];

const toRecord = (
  sources: StudentExamSources,
  klassId: number,
): StudentExamRecord =>
  compact(recordResolvers.map(resolve => resolve(sources))).at(0) ??
  toNotScheduledRecord(sources, klassId);

export const toStudentExamRecords = ({
  studentExams,
  scheduledLessonExams,
  klassId,
}: ToStudentExamRecordsParams): StudentExamRecord[] =>
  groupStudentExamSources({ studentExams, scheduledLessonExams }).map(sources =>
    toRecord(sources, klassId),
  );

export const toPendingStudentExamRecords = (
  studentExams: StudentExamRecordSource[],
): StudentExamRecord[] =>
  compact(
    studentExams.map(studentExam =>
      toPendingRecord({ exam: studentExam.exam, studentExam }),
    ),
  );
